import { useState, useRef } from 'react'
import { Box, Typography, Button, useTheme, useMediaQuery } from '@mui/material'
import Slider from 'react-slick'
import './HeroCarousel.css'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

const HeroCarousel = ({ items }) => {
    const theme = useTheme()
    const [activeSlide, setActiveSlide] = useState(0)
    const [isVideoPlaying, setIsVideoPlaying] = useState(false)
    const sliderRef = useRef(null)

    // Use Material-UI breakpoints for consistent responsive behavior
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

    const settings = {
        dots: true,
        infinite: true,
        speed: 1000,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: !isVideoPlaying,
        autoplaySpeed: 5000,
        fade: true,
        arrows: false,
        dotsClass: 'slick-dots hero-dots',
        beforeChange: (current, next) => {
            setActiveSlide(next)
            setIsVideoPlaying(false)
        },
        afterChange: (current) => {
            setActiveSlide(current)
        }
    }

    const getYouTubeId = (url) => {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
        const match = url.match(regExp)
        return (match && match[2].length === 11) ? match[2] : null
    }

    const handleVideoPlay = () => {
        setIsVideoPlaying(true)
        if (sliderRef.current) {
            sliderRef.current.slickPause()
        }
    }

    const handleVideoPause = () => {
        setIsVideoPlaying(false)
        if (sliderRef.current) {
            sliderRef.current.slickPlay()
        }
    }

    return (
        <Box
            className="hero-carousel-container .hero-dots"
            sx={{
                width: '100%',
                // Calculate height based on 16:9 aspect ratio (1920x1080)
                // Use aspect-ratio CSS property for perfect 16:9 display
                aspectRatio: '16/9',
                maxHeight: {
                    xs: '56.25vw',  // 16:9 aspect ratio: 9/16 * 100 = 56.25vw
                    sm: '56.25vw',
                    md: '56.25vw',
                    lg: '56.25vw',
                    xl: '56.25vw'
                },
                height: {
                    xs: '56.25vw',  // Mobile: Maintain 16:9 aspect ratio
                    sm: '56.25vw',  // Small tablets: Maintain 16:9 aspect ratio
                    md: '56.25vw',  // Desktop: Maintain 16:9 aspect ratio
                    lg: '56.25vw',  // Large desktop: Maintain 16:9 aspect ratio
                    xl: '56.25vw'   // Extra large: Maintain 16:9 aspect ratio
                },
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                // Ensure proper aspect ratio maintenance
                '& .slick-slider': {
                    height: '100%',
                },
                '& .slick-list': {
                    height: '100%',
                },
                '& .slick-track': {
                    height: '100%',
                },
                '& .slick-slide': {
                    height: 'auto',
                    '& > div': {
                        height: '100%',
                    }
                },
                // Custom dots styling with reduced sizes

            }}
        >
            <Slider {...settings} ref={sliderRef}>
                {items.map((item, index) => {
                    const isYouTube = item.video?.includes('youtube.com') || item.video?.includes('youtu.be')
                    const youTubeId = isYouTube ? getYouTubeId(item.video) : null
                    const isActive = activeSlide === index
                    const showContent = !(item.video && isVideoPlaying && isActive)
                    const hasCTA = item.cta && item.cta.trim() !== ''

                    return (
                        <Box
                            key={index}
                            sx={{
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                display: 'flex !important',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                backgroundColor: 'transparent',
                            }}
                        >
                            {item.video ? (
                                isYouTube ? (
                                    <Box
                                        component="iframe"
                                        src={`https://www.youtube.com/embed/${youTubeId}?autoplay=1&mute=1&loop=1&playlist=${youTubeId}&controls=0&disablekb=1&modestbranding=1&rel=0`}
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            border: 'none',
                                            zIndex: 0,
                                        }}
                                        allow="autoplay encrypted-media"
                                        allowFullScreen
                                        onPlay={handleVideoPlay}
                                    />
                                ) : (
                                    <Box
                                        component="video"
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain', // Show full video
                                            zIndex: 0,
                                        }}
                                        onPlay={handleVideoPlay}
                                        onPause={handleVideoPause}
                                        onEnded={handleVideoPause}
                                    >
                                        <source src={item.video} type="video/mp4" />
                                    </Box>
                                )
                            ) : (
                                <>
                                    <Box
                                        component="img"
                                        src={item.image}
                                        alt={item.title}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            // Use contain to show the entire 1920x1080 image
                                            objectFit: 'contain',
                                            objectPosition: 'center',
                                            zIndex: 0,
                                            imageRendering: 'crisp-edges',
                                            transition: 'all 0.3s ease',
                                        }}
                                        loading="lazy"
                                    />
                                    {/* Lighter overlay since we're showing full image */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            background: {
                                                xs: 'linear-gradient(135deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0.1) 50%, rgba(0, 0, 0, 0.05) 100%)',
                                                sm: 'linear-gradient(135deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.08) 50%, rgba(0, 0, 0, 0.03) 100%)',
                                                md: 'linear-gradient(135deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.05) 50%, rgba(0, 0, 0, 0.02) 100%)',
                                            },
                                            zIndex: 1,
                                        }}
                                    />
                                </>
                            )}

                            {/* Content overlay optimized for 16:9 aspect ratio */}
                            {/* {showContent && (
                                <Box sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    zIndex: 2,
                                    padding: {
                                        xs: 1,
                                        sm: 1.5,
                                        md: 2,
                                        lg: 3
                                    }
                                }}>
                                    <Box sx={{
                                        width: '100%',
                                        maxWidth: {
                                            xs: '95%',
                                            sm: '90%',
                                            md: '85%',
                                            lg: '1200px'
                                        },
                                        textAlign: 'center',
                                        margin: '0 auto',
                                    }}>
                                        {item.title && (
                                            <Typography
                                                component="h1"
                                                sx={{
                                                    fontWeight: 700,
                                                    letterSpacing: {
                                                        xs: 0.2,
                                                        sm: 0.3,
                                                        md: 0.5
                                                    },
                                                    mb: {
                                                        xs: 0.5,
                                                        sm: 1,
                                                        md: 1.5
                                                    },
                                                    textShadow: {
                                                        xs: '0 2px 8px rgba(0,0,0,0.9), 0 4px 16px rgba(0,0,0,0.5)',
                                                        sm: '0 2px 10px rgba(0,0,0,0.8), 0 4px 20px rgba(0,0,0,0.4)',
                                                        md: '0 2px 12px rgba(0,0,0,0.7), 0 4px 24px rgba(0,0,0,0.3)'
                                                    },
                                                    lineHeight: {
                                                        xs: 1.0,
                                                        sm: 1.1,
                                                        md: 1.2
                                                    },
                                                    fontSize: {
                                                        xs: 'clamp(0.9rem, 4vw, 1.2rem)',    // Responsive font size
                                                        sm: 'clamp(1.2rem, 5vw, 1.8rem)',
                                                        md: 'clamp(1.8rem, 4vw, 2.5rem)',
                                                        lg: 'clamp(2.2rem, 3.5vw, 3rem)',
                                                        xl: 'clamp(2.5rem, 3vw, 3.5rem)'
                                                    },
                                                    wordBreak: 'break-word',
                                                    hyphens: 'auto'
                                                }}
                                            >
                                                {item.title}
                                            </Typography>
                                        )}

                                        {item.subtitle && (
                                            <Typography
                                                sx={{
                                                    mb: {
                                                        xs: 1,
                                                        sm: 1.5,
                                                        md: 2
                                                    },
                                                    textShadow: {
                                                        xs: '0 1px 6px rgba(0,0,0,0.9), 0 2px 12px rgba(0,0,0,0.5)',
                                                        sm: '0 1px 8px rgba(0,0,0,0.8), 0 2px 16px rgba(0,0,0,0.4)',
                                                        md: '0 1px 10px rgba(0,0,0,0.7), 0 2px 20px rgba(0,0,0,0.3)'
                                                    },
                                                    lineHeight: {
                                                        xs: 1.1,
                                                        sm: 1.2,
                                                        md: 1.3
                                                    },
                                                    fontSize: {
                                                        xs: 'clamp(0.65rem, 3vw, 0.85rem)',   // Responsive font size
                                                        sm: 'clamp(0.8rem, 3.5vw, 1rem)',
                                                        md: 'clamp(1rem, 2.5vw, 1.3rem)',
                                                        lg: 'clamp(1.2rem, 2vw, 1.5rem)',
                                                    },
                                                    maxWidth: {
                                                        xs: '100%',
                                                        sm: '95%',
                                                        md: '85%',
                                                        lg: '800px'
                                                    },
                                                    margin: '0 auto',
                                                    wordBreak: 'break-word',
                                                    opacity: {
                                                        xs: 0.95,
                                                        sm: 0.9,
                                                        md: 0.85
                                                    }
                                                }}
                                            >
                                                {item.subtitle}
                                            </Typography>
                                        )}

                                        {hasCTA && (
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                mt: {
                                                    xs: 1,
                                                    sm: 1.5,
                                                    md: 2
                                                }
                                            }}>
                                                <Button
                                                    variant="contained"
                                                    sx={{
                                                        backgroundColor: theme.palette.primary.main,
                                                        color: 'white',
                                                        fontWeight: 'bold',
                                                        px: {
                                                            xs: 2,
                                                            sm: 2.5,
                                                            md: 4
                                                        },
                                                        py: {
                                                            xs: 0.6,
                                                            sm: 0.8,
                                                            md: 1
                                                        },
                                                        fontSize: {
                                                            xs: 'clamp(0.65rem, 2.5vw, 0.8rem)',  // Responsive font size
                                                            sm: 'clamp(0.75rem, 2.5vw, 0.9rem)',
                                                            md: 'clamp(0.9rem, 2vw, 1.1rem)'
                                                        },
                                                        borderRadius: {
                                                            xs: 1,
                                                            sm: 1.5,
                                                            md: 2
                                                        },
                                                        boxShadow: '0 3px 8px rgba(0,0,0,0.4)',
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.primary.dark,
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                                                        },
                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    }}
                                                >
                                                    {item.cta}
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            )} */}
                        </Box>
                    )
                })}
            </Slider>
        </Box>
    )
}

export default HeroCarousel

